import { useLayoutEffect, useRef, useState } from 'react';
import { Marker } from '@vis.gl/react-maplibre';
import { motion, AnimatePresence } from 'framer-motion';
import { Wifi, WifiOff, Zap, Clock, MapPin, Activity } from 'lucide-react';
import { useMapZoom } from './MapBearingContext';
import type { CabinetDefinition } from '../../types/intersection';
import type { IntersectionTrafficData } from '../../types/traffic';
import type { EstadoVisual } from '../../data/estados-regulador';
import { toDisplayId, toCanonicalId } from '../../utils/canonicalId';
import { useSelection } from '../../store/selection';

const SELECT_COLOR = '#22d3ee'; // cian — color del regulador seleccionado

interface CabinetMarkerProps {
  cabinet: CabinetDefinition;
  estado: EstadoVisual;
  isConnected: boolean;
  currentPlan: number;
  currentPhase: string;
  cyclePosition: number;
  cycleTime: number;
  groupCount: number;
  detectorCount: number;
  trafficData?: IntersectionTrafficData;
  alarmCount?: number;
  forceOpen?: boolean;
}

function formatTime(tenths: number): string {
  const s = Math.floor(tenths / 10);
  const t = tenths % 10;
  return `${s}.${t}s`;
}

export function CabinetMarker({
  cabinet,
  estado,
  isConnected,
  currentPlan,
  currentPhase,
  cyclePosition,
  cycleTime,
  groupCount,
  detectorCount,
  trafficData,
  alarmCount = 0,
  forceOpen = false,
}: CabinetMarkerProps) {
  // Selección centralizada: un único regulador seleccionado a la vez.
  const isSelected = useSelection(s => s.selectedId === cabinet.logicalId);
  const toggle = useSelection(s => s.toggle);
  const isOpen = isSelected || forceOpen;
  const mapZoom = useMapZoom();
  const showDetailed = mapZoom >= 17;
  const scale = Math.min(2, Math.max(0.3, Math.pow(2, (mapZoom - 18.5) * 0.5)));
  const zoneSize = Math.min(120, Math.max(28, (17 - mapZoom) * 22 + 28));
  const hasAlarms = alarmCount > 0;
  const isFlashing = estado.pulse;

  // El popup abre hacia arriba por defecto; si el marcador está cerca del
  // borde superior del viewport (mapa a zoom bajo, municipio entero visible)
  // se corta contra la cabecera. Se mide tras montar y se voltea hacia abajo.
  const popupRef = useRef<HTMLDivElement>(null);
  const [flipDown, setFlipDown] = useState(false);
  useLayoutEffect(() => {
    if (!isOpen) { setFlipDown(false); return; }
    const rect = popupRef.current?.getBoundingClientRect();
    if (rect && rect.top < 0) setFlipDown(true);
  }, [isOpen]);

  return (
    <Marker
      longitude={cabinet.position[0]}
      latitude={cabinet.position[1]}
      anchor={showDetailed ? 'bottom' : 'center'}
      style={{ zIndex: isSelected ? 20 : 10 }}
    >
      {/* `data-highlight` es el enganche que usa DemoHighlights para dibujar
          el anillo. Lo necesita el asistente para señalar un cruce cuando
          habla de él; hasta ahora solo lo llevaban paneles y botones. */}
      <div
        data-highlight={toDisplayId(cabinet.logicalId)}
        className="flex flex-col items-center"
        style={showDetailed ? { transform: `scale(${scale})` } : undefined}
      >
        {/* Popup info */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={popupRef}
              initial={{ opacity: 0, y: 6, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.9 }}
              transition={{ duration: 0.15 }}
              className={`absolute w-56 bg-gray-900/95 backdrop-blur-md border border-gray-600/50 rounded-lg shadow-2xl overflow-hidden z-50 ${
                flipDown ? 'top-full mt-2' : 'bottom-full mb-2'
              }`}
            >
              {/* Header */}
              <div className="px-3 py-2 border-b border-gray-700/50 flex items-center justify-between">
                <div>
                  <div className="font-mono text-xs text-white">ID: {toCanonicalId(cabinet.logicalId)}</div>
                  <div className="font-mono text-[10px] text-gray-500">{cabinet.physicalId}</div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <MapPin className="w-2.5 h-2.5 text-gray-500" />
                    <span className="font-mono text-[10px] text-gray-500">
                      {cabinet.position[1].toFixed(5)}, {cabinet.position[0].toFixed(5)}
                    </span>
                  </div>
                </div>
                {isConnected ? (
                  <Wifi className="w-3.5 h-3.5 text-green-400" />
                ) : (
                  <WifiOff className="w-3.5 h-3.5 text-red-400" />
                )}
              </div>

              {/* Estado */}
              <div className="px-3 py-2 border-b border-gray-700/50">
                <motion.div
                  className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium text-white"
                  style={{ backgroundColor: estado.color }}
                  animate={isFlashing ? { opacity: [1, 0.4, 1] } : {}}
                  transition={isFlashing ? { duration: 1, repeat: Infinity } : {}}
                >
                  {estado.label}
                </motion.div>
              </div>

              {/* Plan + Fase */}
              <div className="px-3 py-2 grid grid-cols-2 gap-2 border-b border-gray-700/50">
                <div>
                  <div className="text-[10px] text-gray-500 flex items-center gap-1">
                    <Zap className="w-2.5 h-2.5" /> Plan
                  </div>
                  <div className="font-mono text-sm text-white">
                    {currentPlan === 0 ? 'EXT' : currentPlan}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] text-gray-500 flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" /> Fase
                  </div>
                  <div className="font-mono text-sm text-white">{currentPhase}</div>
                </div>
              </div>

              {/* Ciclo */}
              {cycleTime > 0 && (
                <div className="px-3 py-2 border-b border-gray-700/50">
                  <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                    <span>Ciclo</span>
                    <span>{formatTime(cyclePosition)} / {formatTime(cycleTime)}</span>
                  </div>
                  <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: estado.color }}
                      animate={{ width: `${(cyclePosition / cycleTime) * 100}%` }}
                      transition={{ duration: 0.1 }}
                    />
                  </div>
                </div>
              )}

              {/* Resumen */}
              <div className="px-3 py-1.5 flex justify-between text-[10px] text-gray-500 border-b border-gray-700/50">
                <span>{groupCount} grupos</span>
                <span>{detectorCount} detectores</span>
              </div>

              {/* Tráfico en tiempo real */}
              {trafficData && trafficData.segments.length > 0 && (
                <div className="px-3 py-2">
                  <div className="flex items-center gap-1 mb-1.5">
                    <Activity className="w-2.5 h-2.5 text-cyan-400" />
                    <span className="text-[10px] text-gray-400">Tráfico en tiempo real</span>
                  </div>
                  <div className="space-y-1">
                    {trafficData.segments.map(seg => {
                      const dotColor =
                        seg.roadClosure ? '#ef4444' :
                        seg.congestionRatio > 0.75 ? '#22c55e' :
                        seg.congestionRatio > 0.5  ? '#f59e0b' :
                        seg.congestionRatio > 0.25 ? '#f97316' : '#ef4444';
                      return (
                        <div key={seg.accessId} className="flex items-center justify-between text-[10px]">
                          <span className="text-gray-400 truncate mr-2">{seg.label}</span>
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-white">
                              {Math.round(seg.currentSpeed)}/{Math.round(seg.freeFlowSpeed)}
                            </span>
                            <span className="text-gray-500">km/h</span>
                            <div
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: dotColor }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {trafficData.lastUpdate && (
                    <div className="text-[9px] text-gray-600 mt-1.5">
                      {trafficData.lastUpdate.toLocaleTimeString()}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {mapZoom >= 17 ? (
          <>
            {/* Gabinete visual (zoom alto) */}
            <motion.div
              onClick={() => toggle(cabinet.logicalId)}
              className="relative cursor-pointer"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
            >
              {/* Halo de selección */}
              {isSelected && (
                <div
                  className="absolute -inset-1.5 rounded-md pointer-events-none"
                  style={{ border: `2px solid ${SELECT_COLOR}`, boxShadow: `0 0 12px rgba(34,211,238,0.85)` }}
                />
              )}

              {/* Glow */}
              <motion.div
                className="absolute -inset-1.5 rounded-md"
                style={{ backgroundColor: estado.glow }}
                animate={isConnected ? { opacity: [0.4, 0.8, 0.4] } : { opacity: 0.2 }}
                transition={isConnected ? { duration: 2, repeat: Infinity } : {}}
              />

              {/* Caja del gabinete */}
              <motion.div
                className="relative w-6 h-8 rounded-sm flex flex-col items-center justify-center gap-0.5"
                style={{
                  backgroundColor: '#1f2937',
                  border: `2px solid ${hasAlarms ? '#ef4444' : estado.color}`,
                  boxShadow: hasAlarms
                    ? '0 0 8px rgba(239,68,68,0.5)'
                    : `0 2px 6px rgba(0,0,0,0.55), 0 0 6px ${estado.glow}`,
                }}
                animate={hasAlarms ? { boxShadow: ['0 0 4px rgba(239,68,68,0.3)', '0 0 12px rgba(239,68,68,0.6)', '0 0 4px rgba(239,68,68,0.3)'] } : {}}
                transition={hasAlarms ? { duration: 1.5, repeat: Infinity } : {}}
              >
                {/* LEDs de estado */}
                <motion.div
                  className="w-1 h-1 rounded-full"
                  style={{ backgroundColor: isConnected ? '#22c55e' : '#ef4444' }}
                  animate={isConnected ? { opacity: [1, 0.3, 1] } : {}}
                  transition={isConnected ? { duration: 1.5, repeat: Infinity } : {}}
                />
                <div className="w-3 h-0.5 rounded-full bg-gray-600" />
                <div className="w-3 h-0.5 rounded-full bg-gray-600" />
              </motion.div>

              {/* Antena */}
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-[1px] h-2 bg-gray-400" />
              <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-gray-400" />

              {/* Alarm badge */}
              {hasAlarms && (
                <motion.div
                  className="absolute -top-1 -right-2 min-w-[14px] h-[14px] rounded-full bg-red-500 flex items-center justify-center"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <span className="text-[8px] font-bold text-white leading-none">{alarmCount}</span>
                </motion.div>
              )}
            </motion.div>

            {/* Sombra */}
            <div className="w-4 h-1 mt-0.5 rounded-full bg-black/25 blur-[1px]" />
          </>
        ) : (
          <motion.div
            onClick={() => toggle(cabinet.logicalId)}
            className="relative cursor-pointer flex items-center justify-center"
            style={{ width: zoneSize, height: zoneSize }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
          >
            {/* Halo de selección */}
            {isSelected && (
              <div
                className="absolute rounded-full pointer-events-none z-20"
                style={{
                  width: 26, height: 26,
                  border: `2.5px solid ${SELECT_COLOR}`,
                  boxShadow: `0 0 12px rgba(34,211,238,0.9)`,
                }}
              />
            )}

            {/* Outer pulsing ring */}
            <motion.div
              className="absolute inset-0 rounded-full"
              style={{
                border: `2px solid ${estado.color}`,
                backgroundColor: estado.glow,
              }}
              animate={isConnected ? { opacity: [0.25, 0.5, 0.25], scale: [1, 1.08, 1] } : { opacity: 0.28 }}
              transition={isConnected ? { duration: 3, repeat: Infinity, ease: 'easeInOut' } : {}}
            />
            {/* Inner solid dot — borde claro + sombra para destacar sobre el
                grisáceo del mapa 3D (un regulador offline debe verse "presente
                pero apagado", no perderse). */}
            <div
              className="relative w-3.5 h-3.5 rounded-full z-10"
              style={{
                backgroundColor: estado.color,
                border: '1.5px solid rgba(255,255,255,0.92)',
                boxShadow: isConnected
                  ? `0 1px 4px rgba(0,0,0,0.55), 0 0 8px ${estado.glow}`
                  : '0 1px 4px rgba(0,0,0,0.55)',
              }}
            />
            {/* Label */}
            <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 whitespace-nowrap">
              <span className="text-[9px] font-mono px-1 py-0.5 rounded bg-gray-900/80 text-gray-300">
                {toDisplayId(cabinet.logicalId)}
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </Marker>
  );
}
